'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetPublicPartnerCreatorRow } from '@morfi/backend-sdk-v2/client';
import { useAuth, useGetFollowsCounts } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { usePortfolioContext } from '@/components/portfolio/portfolio-context';
import { FollowButton } from '@/components/profile/follow-button';
import { FollowersDialog } from '@/components/profile/followers-dialog';
import { UserIdentity } from '@/components/profile/user-identity';
import { ProfileStreakStat } from '@/components/streaks/profile-streak-stat';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { Agreement01IconSolid, GiftIcon, LinkForwardIconSolid, Settings01Icon } from '@/components/ui/icon/glyphs';
import { IdentityPill } from '@/components/ui/identity-pill';
import { Panel } from '@/components/ui/panel';
import { onEvent } from '@/events';
import { Link } from '@/i18n/navigation';
import { checkGate } from '@/lib/analytics';
import { useImpersonationStore } from '@/lib/impersonation/store';
import { withDemoMode } from '@/lib/network';
import { useUserNetwork } from '@/lib/network/client';
import { ShareModal } from '@/lib/share';
import { cn } from '@/lib/ui/utils';
import { PartnerProfileDetails } from '../partner/partner-profile-details';
import { ProfilePortfolioImages } from './profile-portfolio-images';
interface ConnectedProvider {
  provider: string;
  username?: string;
  displayName?: string;
}
export interface ProfilePortfolioCardProps {
  partnerProfile?: GetPublicPartnerCreatorRow;
  /** Wallets whose trades drive the streak stat. */
  walletPubkeys?: readonly string[];
  /** Display name to render. Sourced from the public-user data path. */
  displayName: string | null;
  /** @-handle. Sourced from the public-user data path. */
  handle: string | null;
  bio: string | null;
  /** Photo URL — empty/null falls through to deterministic-palette initials. */
  avatarUrl: string | null;
  /** Spec-071 NSFW flag for the avatar. */
  avatarNsfw?: boolean | null;
  /** `users.avatar_preferences.rasterise` — drives the dither-vs-raw render. */
  avatarRasterise?: boolean | null;
  /** Hero / cover URL — empty/null falls through to user-seeded gradient. */
  heroUrl: string | null;
  /** Spec-071 NSFW flag for the hero. */
  coverNsfw?: boolean | null;
  /** Provider badge list (X / Google / Apple). */
  connectedProviders: ConnectedProvider[];
  /** Outer className passthrough — typically `min-w-0` from the parent grid. */
  className?: string;
}
function buildProfileShareUrl(userId: string, network: 'mainnet' | 'devnet'): string {
  if (typeof window === 'undefined') return '';
  const u = new URL(`/portfolio/${userId}`, window.location.origin);
  withDemoMode(u, network);
  return u.toString();
}

/**
 * `<ProfilePortfolioCard>` — compact left-column card (~40% width beside the PnL
 * hero at md+) at the top of `/portfolio` and `/portfolio/[user_id]`, paired
 * with the sibling PnL card (~60%). Surfaces hero, 96 px avatar, identity,
 * share button, and an inline Followers / Following row.
 *
 * Owner viewers see inline-edit affordances on hero and avatar; impersonating
 * engineers see the read-only branch (writes are also blocked at the SDK
 * boundary by `useGuardedSdkMutation`).
 *
 * Spec: 081-profile-portfolio-card
 */
export function ProfilePortfolioCard(props) {
  const $ = _c(136);
  const {
    partnerProfile,
    walletPubkeys,
    displayName,
    handle,
    bio,
    avatarUrl,
    avatarNsfw,
    avatarRasterise,
    heroUrl,
    coverNsfw,
    connectedProviders,
    className
  } = props;
  const t = useTranslations("profile.next.portfolio_card");
  const tProfile = useTranslations("profile");
  const tPartner = useTranslations("profile.partner");
  const {
    viewerMode,
    userId
  } = usePortfolioContext();
  const isOwn = viewerMode === "own";
  const {
    user,
    setUser
  } = useAuth();
  const {
    network
  } = useUserNetwork();
  const isImpersonating = useImpersonationStore(_temp);
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followersTab, setFollowersTab] = useState("followers");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["followers", "following"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== userId) {
    t1 = {
      userId,
      kind: "counts",
      fields: t0
    };
    $[1] = userId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const followCounts = useGetFollowsCounts(t1);
  const followersCount = followCounts.isPending ? null : followCounts.data?.followers ?? 0;
  const followingCount = followCounts.isPending ? null : followCounts.data?.following ?? 0;
  const t2 = network === "mainnet" ? "mainnet" : "devnet";
  let t3;
  if ($[3] !== t2 || $[4] !== userId) {
    t3 = buildProfileShareUrl(userId, t2);
    $[3] = t2;
    $[4] = userId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const shareUrl = t3;
  let t4;
  if ($[6] !== displayName || $[7] !== handle || $[8] !== tProfile) {
    t4 = handle !== null && handle.length > 0 ? tProfile("share_text", {
      recipientHandle: `@${handle}`
    }) : displayName !== null && displayName.length > 0 ? tProfile("share_text", {
      recipientHandle: displayName
    }) : tProfile("share_text_anonymous");
    $[6] = displayName;
    $[7] = handle;
    $[8] = tProfile;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const shareText = t4;
  const showOwnerEdit = isOwn && user !== null && !isImpersonating && partnerProfile === undefined;
  let t5;
  if ($[10] !== isOwn) {
    t5 = isOwn && checkGate("rewards_platform");
    $[10] = isOwn;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const showRewards = t5;
  let t6;
  if ($[12] !== setUser || $[13] !== user) {
    t6 = function handleAvatarChange(next, t7) {
      const avatarNsfw_0 = t7 === undefined ? false : t7;
      if (user === null) {
        return;
      }
      setUser({
        ...user,
        avatarUrl: next.length > 0 ? next : null,
        avatarNsfw: avatarNsfw_0
      });
    };
    $[12] = setUser;
    $[13] = user;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleAvatarChange = t6;
  let t7;
  if ($[15] !== setUser || $[16] !== user) {
    t7 = function handleHeroChange(next_0) {
      if (user === null) {
        return;
      }
      setUser({
        ...user,
        heroUrl: next_0.length > 0 ? next_0 : null
      });
    };
    $[15] = setUser;
    $[16] = user;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleHeroChange = t7;
  let t8;
  if ($[18] !== userId || $[19] !== viewerMode) {
    t8 = function handleShareClick() {
      onEvent("profile_portfolio_card_share_clicked", {
        viewerMode,
        targetUserId: userId
      });
    };
    $[18] = userId;
    $[19] = viewerMode;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleShareClick = t8;
  let t9;
  if ($[21] !== userId || $[22] !== viewerMode) {
    t9 = function handleFollowersClick() {
      setFollowersTab("followers");
      setFollowersOpen(true);
      onEvent("profile_portfolio_card_kpi_clicked", {
        viewerMode,
        targetUserId: userId,
        kpi: "followers"
      });
    };
    $[21] = userId;
    $[22] = viewerMode;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const handleFollowersClick = t9;
  let t10;
  if ($[24] !== userId || $[25] !== viewerMode) {
    t10 = function handleFollowingClick() {
      setFollowersTab("following");
      setFollowersOpen(true);
      onEvent("profile_portfolio_card_kpi_clicked", {
        viewerMode,
        targetUserId: userId,
        kpi: "following"
      });
    };
    $[24] = userId;
    $[25] = viewerMode;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const handleFollowingClick = t10;
  let t11;
  if ($[27] !== className) {
    t11 = cn("relative gap-0 pt-0", className);
    $[27] = className;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = user?.avatarPreferences?.rasterise ?? false;
  let t13;
  if ($[29] !== avatarNsfw || $[30] !== avatarRasterise || $[31] !== avatarUrl || $[32] !== coverNsfw || $[33] !== displayName || $[34] !== handleAvatarChange || $[35] !== handleHeroChange || $[36] !== heroUrl || $[37] !== partnerProfile || $[38] !== showOwnerEdit || $[39] !== t12 || $[40] !== userId) {
    t13 = <ProfilePortfolioImages partnerProfile={partnerProfile} userId={userId} displayName={displayName} avatarUrl={avatarUrl} heroUrl={heroUrl} avatarNsfw={avatarNsfw} coverNsfw={coverNsfw} avatarRasterise={avatarRasterise} ownerRasterise={t12} showOwnerEdit={showOwnerEdit} handleHeroChange={handleHeroChange} handleAvatarChange={handleAvatarChange} />;
    $[29] = avatarNsfw;
    $[30] = avatarRasterise;
    $[31] = avatarUrl;
    $[32] = coverNsfw;
    $[33] = displayName;
    $[34] = handleAvatarChange;
    $[35] = handleHeroChange;
    $[36] = heroUrl;
    $[37] = partnerProfile;
    $[38] = showOwnerEdit;
    $[39] = t12;
    $[40] = userId;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== userId || $[43] !== viewerMode) {
    t14 = viewerMode === "other" ? <FollowButton targetUserId={userId} /> : null;
    $[42] = userId;
    $[43] = viewerMode;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== showRewards || $[46] !== t) {
    t15 = showRewards ? <ProfileRewardsButton ariaLabel={t("rewards_label")} /> : null;
    $[45] = showRewards;
    $[46] = t;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== isOwn || $[49] !== t) {
    t16 = isOwn ? <ProfileSettingsButton ariaLabel={t("settings_label")} /> : null;
    $[48] = isOwn;
    $[49] = t;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t) {
    t17 = t("share_title");
    $[51] = t;
    $[52] = t17;
  } else {
    t17 = $[52];
  }
  let t18;
  if ($[53] !== t) {
    t18 = t("share_label");
    $[53] = t;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] !== handleShareClick || $[56] !== shareText || $[57] !== shareUrl || $[58] !== t17 || $[59] !== t18 || $[60] !== userId) {
    t19 = <ProfileShareTrigger userId={userId} url={shareUrl} text={shareText} title={t17} ariaLabel={t18} onClick={handleShareClick} />;
    $[55] = handleShareClick;
    $[56] = shareText;
    $[57] = shareUrl;
    $[58] = t17;
    $[59] = t18;
    $[60] = userId;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== t14 || $[63] !== t15 || $[64] !== t16 || $[65] !== t19) {
    t20 = <div className="flex items-center justify-end gap-2 pl-4 pt-2 pr-3 sm:pl-6 sm:pr-3">{t14}{t15}{t16}{t19}</div>;
    $[62] = t14;
    $[63] = t15;
    $[64] = t16;
    $[65] = t19;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  const t21 = `/portfolio/${userId}`;
  const t22 = displayName ?? "";
  let t23;
  if ($[67] !== t || $[68] !== t22) {
    t23 = t("open_profile_aria", {
      displayName: t22
    });
    $[67] = t;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== avatarNsfw || $[71] !== avatarUrl || $[72] !== connectedProviders || $[73] !== displayName || $[74] !== handle || $[75] !== partnerProfile || $[76] !== tPartner || $[77] !== userId) {
    t24 = partnerProfile ? <><div className="flex flex-wrap items-center gap-2.5"><h1 className="min-w-0"><UserIdentity user={{
            userId,
            displayName: partnerProfile.displayName,
            handle: partnerProfile.handle,
            avatarUrl: partnerProfile.avatarUrl,
            avatarNsfw: avatarNsfw ?? null,
            connectedProviders: []
          }} displayNameOverride={partnerProfile.displayName} showHandle={false} nameClassName="text-heading-lg" /></h1><IdentityPill tone="partner" icon={Agreement01IconSolid}>{tPartner("badge")}</IdentityPill></div><p className="break-words text-caption text-foreground-tertiary">@{partnerProfile.handle}{partnerProfile.location ? ` · ${partnerProfile.location}` : ""}</p></> : <UserIdentity user={{
      userId,
      displayName,
      handle,
      avatarUrl,
      connectedProviders
    }} variant="stack" size="md" showProviderBadges={true} nameClassName="text-heading-md" handleClassName="text-caption" />;
    $[70] = avatarNsfw;
    $[71] = avatarUrl;
    $[72] = connectedProviders;
    $[73] = displayName;
    $[74] = handle;
    $[75] = partnerProfile;
    $[76] = tPartner;
    $[77] = userId;
    $[78] = t24;
  } else {
    t24 = $[78];
  }
  let t25;
  if ($[79] !== t21 || $[80] !== t23 || $[81] !== t24) {
    t25 = <Link href={t21} aria-label={t23} className="flex min-w-0 flex-col gap-0.5 px-4 pb-0 pt-5 sm:px-6">{t24}</Link>;
    $[79] = t21;
    $[80] = t23;
    $[81] = t24;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  let t26;
  if ($[83] !== bio || $[84] !== partnerProfile) {
    t26 = partnerProfile !== undefined ? <PartnerProfileDetails profile={partnerProfile} /> : bio?.trim() ? <p className="mt-4 whitespace-pre-wrap break-words px-4 text-body text-foreground-secondary sm:px-6">{bio}</p> : null;
    $[83] = bio;
    $[84] = partnerProfile;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  const t27 = followersCount ?? 0;
  let t28;
  if ($[86] !== t || $[87] !== t27) {
    t28 = t("followers_aria", {
      count: t27
    });
    $[86] = t;
    $[87] = t27;
    $[88] = t28;
  } else {
    t28 = $[88];
  }
  let t29;
  if ($[89] !== followersCount) {
    t29 = followersCount === null ? <Skeleton className="h-5 w-8" /> : <span className="text-body font-medium tabular-nums text-foreground-primary">{followersCount}</span>;
    $[89] = followersCount;
    $[90] = t29;
  } else {
    t29 = $[90];
  }
  let t30;
  if ($[91] !== t) {
    t30 = t("followers_label");
    $[91] = t;
    $[92] = t30;
  } else {
    t30 = $[92];
  }
  let t31;
  if ($[93] !== t30) {
    t31 = <span className="text-body font-normal text-foreground-tertiary">{t30}</span>;
    $[93] = t30;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  let t32;
  if ($[95] !== handleFollowersClick || $[96] !== t28 || $[97] !== t29 || $[98] !== t31) {
    t32 = <button type="button" onClick={handleFollowersClick} aria-label={t28} className="inline-flex items-baseline gap-1 rounded-item transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t29}{t31}</button>;
    $[95] = handleFollowersClick;
    $[96] = t28;
    $[97] = t29;
    $[98] = t31;
    $[99] = t32;
  } else {
    t32 = $[99];
  }
  const t33 = followingCount ?? 0;
  let t34;
  if ($[100] !== t || $[101] !== t33) {
    t34 = t("following_aria", {
      count: t33
    });
    $[100] = t;
    $[101] = t33;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== followingCount) {
    t35 = followingCount === null ? <Skeleton className="h-5 w-8" /> : <span className="text-body font-medium tabular-nums text-foreground-primary">{followingCount}</span>;
    $[103] = followingCount;
    $[104] = t35;
  } else {
    t35 = $[104];
  }
  let t36;
  if ($[105] !== t) {
    t36 = t("following_label");
    $[105] = t;
    $[106] = t36;
  } else {
    t36 = $[106];
  }
  let t37;
  if ($[107] !== t36) {
    t37 = <span className="text-body font-normal text-foreground-tertiary">{t36}</span>;
    $[107] = t36;
    $[108] = t37;
  } else {
    t37 = $[108];
  }
  let t38;
  if ($[109] !== handleFollowingClick || $[110] !== t34 || $[111] !== t35 || $[112] !== t37) {
    t38 = <button type="button" onClick={handleFollowingClick} aria-label={t34} className="inline-flex items-baseline gap-1 rounded-item transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-primary">{t35}{t37}</button>;
    $[109] = handleFollowingClick;
    $[110] = t34;
    $[111] = t35;
    $[112] = t37;
    $[113] = t38;
  } else {
    t38 = $[113];
  }
  let t39;
  if ($[114] !== partnerProfile || $[115] !== userId || $[116] !== walletPubkeys) {
    t39 = partnerProfile === undefined ? <ProfileStreakStat userId={userId} walletPubkeys={walletPubkeys} showZero={true} /> : null;
    $[114] = partnerProfile;
    $[115] = userId;
    $[116] = walletPubkeys;
    $[117] = t39;
  } else {
    t39 = $[117];
  }
  let t40;
  if ($[118] !== t32 || $[119] !== t38 || $[120] !== t39) {
    t40 = <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 px-4 pb-0 sm:px-6">{t32}{t38}{t39}</div>;
    $[118] = t32;
    $[119] = t38;
    $[120] = t39;
    $[121] = t40;
  } else {
    t40 = $[121];
  }
  let t41;
  if ($[122] !== t11 || $[123] !== t13 || $[124] !== t20 || $[125] !== t25 || $[126] !== t26 || $[127] !== t40) {
    t41 = <Panel className={t11}>{t13}{t20}{t25}{t26}{t40}</Panel>;
    $[122] = t11;
    $[123] = t13;
    $[124] = t20;
    $[125] = t25;
    $[126] = t26;
    $[127] = t40;
    $[128] = t41;
  } else {
    t41 = $[128];
  }
  let t42;
  if ($[129] !== followersOpen || $[130] !== followersTab || $[131] !== userId) {
    t42 = <FollowersDialog open={followersOpen} onOpenChange={setFollowersOpen} userId={userId} defaultTab={followersTab} />;
    $[129] = followersOpen;
    $[130] = followersTab;
    $[131] = userId;
    $[132] = t42;
  } else {
    t42 = $[132];
  }
  let t43;
  if ($[133] !== t41 || $[134] !== t42) {
    t43 = <>{t41}{t42}</>;
    $[133] = t41;
    $[134] = t42;
    $[135] = t43;
  } else {
    t43 = $[135];
  }
  return t43;
}

// ---------------------------------------------------------------------------
// Internal — share trigger split into a child for readability.
// ---------------------------------------------------------------------------
function _temp(s) {
  return s.isActive;
}
interface ProfileShareTriggerProps {
  userId: string;
  url: string;
  text: string;
  title: string;
  ariaLabel: string;
  onClick: () => void;
}
function ProfileShareTrigger(props) {
  const $ = _c(12);
  let t0;
  if ($[0] !== props.userId) {
    t0 = {
      kind: "profile",
      userId: props.userId
    };
    $[0] = props.userId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Icon icon={LinkForwardIconSolid} className="size-4" aria-hidden="true" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props.ariaLabel || $[4] !== props.onClick) {
    t2 = <AnimatedButton variant="secondary" size="default" aria-label={props.ariaLabel} onClick={props.onClick}>{t1}</AnimatedButton>;
    $[3] = props.ariaLabel;
    $[4] = props.onClick;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props.text || $[7] !== props.title || $[8] !== props.url || $[9] !== t0 || $[10] !== t2) {
    t3 = <ShareModal surface={t0} url={props.url} text={props.text} title={props.title} trigger={t2} />;
    $[6] = props.text;
    $[7] = props.title;
    $[8] = props.url;
    $[9] = t0;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}

/** Gear button — own-profile only, mirrors the share trigger and links to settings.
 *  `w-9 px-0` reproduces the share button's icon-only square (`size-9`); the
 *  auto square-detection only fires on a direct `<Icon>` child, not through the
 *  `asChild` <Link>. */
function ProfileSettingsButton(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/settings"><Icon icon={Settings01Icon} className="size-4" aria-hidden="true" /></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.ariaLabel) {
    t1 = <AnimatedButton variant="secondary" size="default" aria-label={props.ariaLabel} asChild={true} className="w-9 px-0">{t0}</AnimatedButton>;
    $[1] = props.ariaLabel;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/** Rewards shortcut — own-profile only and hidden with the rewards platform gate. */
function ProfileRewardsButton(props) {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Link href="/rewards" onClick={_temp2}><Icon icon={GiftIcon} className="size-4" aria-hidden="true" /></Link>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.ariaLabel) {
    t1 = <AnimatedButton variant="secondary" size="default" aria-label={props.ariaLabel} asChild={true} className="w-9 px-0">{t0}</AnimatedButton>;
    $[1] = props.ariaLabel;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function _temp2() {
  onEvent("rewards_entry_clicked", {
    authenticated: true,
    source: "portfolio"
  });
}